import React, { useEffect } from 'react';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface NotificationAction {
  label: string;
  onClick: () => void | Promise<void>;
  variant?: 'primary' | 'danger' | 'secondary';
}

export interface NotificationData {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
  duration?: number;
  persistentId?: string;
  actions?: NotificationAction[];
}

interface NotificationProps {
  notification: NotificationData;
  onClose: (id: string) => void;
}

const Notification: React.FC<NotificationProps> = ({ notification, onClose }) => {
  const { id, type, title, message, duration = 5000, actions } = notification;

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose(id);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [id, duration, onClose]);

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return 'bg-green-600 border-green-500';
      case 'error':
        return 'bg-red-600 border-red-500';
      case 'warning':
        return 'bg-yellow-600 border-yellow-500';
      case 'info':
        return 'bg-blue-600 border-blue-500';
      default:
        return 'bg-gray-600 border-gray-500';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      default:
        return '📢';
    }
  };

  const actionClass = (variant?: NotificationAction['variant']) => {
    switch (variant) {
      case 'danger':
        return 'bg-red-800 hover:bg-red-700 border-red-500';
      case 'secondary':
        return 'bg-gray-700 hover:bg-gray-600 border-gray-500';
      default:
        return 'bg-white/20 hover:bg-white/30 border-white/40';
    }
  };

  return (
    <div className={`${getTypeStyles()} border-l-4 p-4 mb-3 rounded-md shadow-lg text-white animate-slide-in`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <span className="text-lg">{getIcon()}</span>
        </div>
        <div className="ml-3 flex-grow min-w-0">
          <h3 className="text-sm font-medium">{title}</h3>
          {message && <p className="mt-1 text-sm opacity-90 whitespace-pre-line">{message}</p>}
          {actions && actions.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {actions.map((action) => (
                <button
                  key={action.label}
                  type="button"
                  onClick={() => void action.onClick()}
                  className={`text-xs font-semibold px-3 py-1.5 rounded border ${actionClass(action.variant)}`}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {!(actions && actions.length > 0 && duration === 0) && (
          <div className="flex-shrink-0 ml-4">
            <button
              onClick={() => onClose(id)}
              className="text-white hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 rounded-full p-1"
              aria-label="Закрыть уведомление"
            >
              <span className="text-lg">✕</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notification;
